// src/components/AnalystRatings.js
import React, { useEffect, useState } from 'react';

const RATING_COLORS = {
  strongBuy: 'bg-green-600',
  buy: 'bg-green-400',
  hold: 'bg-yellow-400',
  sell: 'bg-red-400',
  strongSell: 'bg-red-600',
};

const RATING_LABELS = {
  strongBuy: 'Strong Buy',
  buy: 'Buy',
  hold: 'Hold',
  sell: 'Sell',
  strongSell: 'Strong Sell',
};

const AnalystRatings = ({ stockSymbol }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!stockSymbol) return;

    const fetchAnalyst = async () => {
      setLoading(true);
      try {
        const resp = await fetch(
          `https://metrics-8.onrender.com/api/analyst/${encodeURIComponent(stockSymbol)}`
        );
        if (resp.ok) {
          setData(await resp.json());
        }
      } catch (e) {
        console.warn('[AnalystRatings] fetch error:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyst();
  }, [stockSymbol]);

  if (!stockSymbol) return null;
  if (loading) return <div className="text-sm text-gray-500">Loading analyst data…</div>;
  if (!data) return null;
  if (data.error) {
    return (
      <div className="card p-5">
        <h3 className="text-base font-semibold text-gray-800 mb-2">Analyst Consensus</h3>
        <p className="text-sm text-gray-500">Analyst data temporarily unavailable. Try refreshing.</p>
      </div>
    );
  }

  const {
    recommendation,
    targetMeanPrice,
    targetHighPrice,
    targetLowPrice,
    currentPrice,
    upside,
    numberOfAnalysts,
    ratingBreakdown,
  } = data;

  const totalRatings = Object.values(ratingBreakdown || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="card p-5">
      <h3 className="text-base font-semibold text-gray-800 mb-4">Analyst Consensus</h3>

      {/* Recommendation badge */}
      <div className="flex items-center gap-3 mb-4">
        <span
          className={`px-3 py-1.5 rounded-lg text-sm font-bold text-white ${
            recommendation.toLowerCase().includes('buy')
              ? 'bg-green-600'
              : recommendation.toLowerCase().includes('sell')
              ? 'bg-red-600'
              : 'bg-yellow-500'
          }`}
        >
          {recommendation}
        </span>
        {numberOfAnalysts && (
          <span className="text-xs text-gray-500">
            Based on {numberOfAnalysts} analyst{numberOfAnalysts > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Price targets */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-surface-50 rounded-lg p-3 text-center">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Low</div>
          <div className="text-sm font-semibold text-gray-700">
            {targetLowPrice != null ? `$${targetLowPrice.toFixed(2)}` : '—'}
          </div>
        </div>
        <div className="bg-brand-50 rounded-lg p-3 text-center border border-brand-200">
          <div className="text-[10px] text-brand-600 uppercase tracking-wide font-medium">Mean Target</div>
          <div className="text-lg font-bold text-brand-700">
            {targetMeanPrice != null ? `$${targetMeanPrice.toFixed(2)}` : '—'}
          </div>
        </div>
        <div className="bg-surface-50 rounded-lg p-3 text-center">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">High</div>
          <div className="text-sm font-semibold text-gray-700">
            {targetHighPrice != null ? `$${targetHighPrice.toFixed(2)}` : '—'}
          </div>
        </div>
      </div>

      {/* Upside indicator */}
      {upside != null && currentPrice != null && (
        <div className="flex items-center gap-2 mb-4 bg-surface-50 rounded-lg px-3 py-2">
          <span className="text-xs text-gray-500">Current: ${currentPrice.toFixed(2)}</span>
          <span className="text-xs text-gray-300">→</span>
          <span className={`text-sm font-semibold ${upside >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {upside >= 0 ? '↑' : '↓'} {Math.abs(upside).toFixed(1)}% {upside >= 0 ? 'upside' : 'downside'}
          </span>
        </div>
      )}

      {/* Rating breakdown bar */}
      {totalRatings > 0 && (
        <div>
          <div className="flex rounded-full overflow-hidden h-3 mb-2">
            {Object.entries(RATING_COLORS).map(([key, color]) => {
              const count = ratingBreakdown[key] || 0;
              const pct = (count / totalRatings) * 100;
              if (pct === 0) return null;
              return (
                <div
                  key={key}
                  className={`${color} transition-all`}
                  style={{ width: `${pct}%` }}
                  title={`${RATING_LABELS[key]}: ${count}`}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-gray-500">
            {Object.entries(RATING_LABELS).map(([key, label]) => {
              const count = ratingBreakdown[key] || 0;
              if (count === 0) return null;
              return (
                <span key={key}>
                  {label} ({count})
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalystRatings;
